import { useMemo, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, day as dayText } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { useAction, useSave } from '../manage/common.tsx';
import { Avatar } from './Directory.tsx';

/**
 * Reviews and goals. A review asks the person and their reviewer the same questions; neither sees
 * the other's answers until both have sent theirs. Then they meet, the reviewer closes the review
 * with an overall rating and a summary, and the person confirms they have read it. Goals sit beside
 * the reviews: what a person works towards, kept up to date by them and the people above them.
 */

type State = 'open' | 'shared' | 'done' | 'cancelled';
interface Row {
  id: string;
  name: string;
  employee: { id: string; name: string };
  manager: { id: string; name: string };
  title: string;
  due: string | null;
  overdue: boolean;
  state: State;
  self_done: boolean;
  manager_done: boolean;
  meeting_at: string | null;
  rating: number | null;
  acknowledged: boolean;
  closed_at: string | null;
}
interface List {
  today: string;
  reviews: Row[];
  counts: { mine: number; to_read: number; team: number };
  can: { manage: boolean; team: boolean };
}
interface Question {
  id: string;
  kind: 'rating' | 'text';
  text: string;
  manager_only: boolean;
}
type Answers = Record<string, { rating?: number | null; text?: string | null }>;
interface Goal {
  id: string;
  user: string;
  title: string;
  description: string | null;
  due: string | null;
  progress: number;
  state: 'open' | 'done' | 'cancelled';
  overdue?: boolean;
}
interface Detail {
  id: string;
  name: string;
  title: string;
  intro: string | null;
  state: State;
  due: string | null;
  employee: { id: string; name: string; job_title: string | null; department: string | null; photo: string | null };
  manager: { id: string; name: string };
  me: 'employee' | 'manager' | 'admin';
  questions: Question[];
  self: { done_at: string | null; answers: Answers | null };
  reviewer: { done_at: string | null; answers: Answers | null };
  meeting_at: string | null;
  rating: number | null;
  summary: string | null;
  private_note: string | null;
  closed_at: string | null;
  acknowledged_at: string | null;
  employee_comment: string | null;
  cancel_reason: string | null;
  goals: Goal[];
  can: { answer: boolean; close: boolean; plan: boolean; acknowledge: boolean; cancel: boolean; reopen: boolean; goals: boolean };
}
interface Template {
  id: string;
  name: string;
  intro: string | null;
  questions: Question[];
  active: boolean;
  reviews: number;
}
type View = 'mine' | 'team' | 'all' | 'goals' | 'templates';
type Filter = 'active' | 'done' | 'cancelled';

const pad = (n: number) => String(n).padStart(2, '0');
const localInput = (at: Date) => `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
const RATINGS = [1, 2, 3, 4, 5] as const;
const ratingLabel = (n: number) => t(`rev.rating_${n}` as MessageKey);

/** What the row says comes next, from the point of view of whoever looks at it. */
function nextStep(r: Row, view: View, me: string | undefined): { tone: Tone; icon: IconName; label: string } {
  const mine = r.employee.id === me;
  const reviewer = r.manager.id === me;
  if (r.state === 'cancelled') return { tone: 'neutral', icon: 'x', label: t('rev.s_cancelled') };
  if (r.state === 'done') {
    if (r.acknowledged) return { tone: 'ok', icon: 'checkCircle', label: t('rev.s_confirmed') };
    return mine ? { tone: 'warn', icon: 'eye', label: t('rev.s_read_confirm') } : { tone: 'info', icon: 'clock', label: t('rev.s_wait_confirm', { name: r.employee.name }) };
  }
  if (r.state === 'shared') return reviewer && view !== 'mine' ? { tone: 'warn', icon: 'calendar', label: t('rev.s_meet_close') } : { tone: 'info', icon: 'users', label: t('rev.s_ready_to_talk') };
  if ((mine && !r.self_done) || (reviewer && view !== 'mine' && !r.manager_done)) return { tone: 'warn', icon: 'edit', label: t('rev.s_your_answers') };
  const waiting = !r.self_done && !r.manager_done ? t('rev.s_both') : !r.self_done ? r.employee.name : r.manager.name;
  return { tone: 'neutral', icon: 'clock', label: t('rev.s_waiting_for', { name: waiting }) };
}

function Steps({ self, reviewer, meeting, closed, compact }: { self: boolean; reviewer: boolean; meeting: boolean; closed: boolean; compact?: boolean }) {
  const steps: [boolean, MessageKey][] = [
    [self, 'rev.step_self'],
    [reviewer, 'rev.step_reviewer'],
    [meeting, 'rev.step_meeting'],
    [closed, 'rev.step_closed'],
  ];
  return (
    <ol className={`rev-steps${compact ? ' compact' : ''}`} aria-label={t('rev.progress')}>
      {steps.map(([done, key]) => (
        <li key={key} className={done ? 'done' : ''}>
          <span className="rev-step-dot" aria-hidden="true">
            {done ? <Icon name="check" size={12} /> : null}
          </span>
          <span className="rev-step-label">
            {t(key)}
            <span className="sr-only"> · {done ? t('rev.step_done') : t('rev.step_not_yet')}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

export function Stars({ value, size = 16 }: { value: number | null | undefined; size?: number }) {
  if (!value) return <span className="muted">—</span>;
  return (
    <span className="rev-stars" role="img" aria-label={t('rev.rated', { n: value, label: ratingLabel(value) })}>
      {RATINGS.map((n) => (
        <span key={n} className={n <= value ? 'on' : ''}>
          <Icon name="star" size={size} />
        </span>
      ))}
    </span>
  );
}

function RatingInput({ value, onChange, label, invalid }: { value: number | null | undefined; onChange: (n: number) => void; label: string; invalid?: boolean }) {
  return (
    <div className={`rev-rating${invalid ? ' invalid' : ''}`} role="radiogroup" aria-label={label}>
      {RATINGS.map((n) => (
        <button key={n} type="button" role="radio" aria-checked={value === n} className={value === n ? 'selected' : ''} onClick={() => onChange(n)}>
          <strong>{n}</strong>
          <span>{ratingLabel(n)}</span>
        </button>
      ))}
      {/* On a phone the tiles show only the number; the chosen word is written below them. */}
      <span className="rev-rating-word" aria-hidden="true">
        {value ? ratingLabel(value) : ' '}
      </span>
    </div>
  );
}

// ---- the tab ------------------------------------------------------------------------------

export function ReviewsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { me, can } = useSession();
  const manage = can('hr.manage');
  const [view, setView] = useState<View>('mine');
  const [filter, setFilter] = useState<Filter>('active');
  const listView = view === 'mine' || view === 'team' || view === 'all';
  const { data, reload } = useAction<List>('hr.reviews', { view: listView ? view : 'mine', state: filter });
  const [open, setOpen] = useState<string | null>(null);
  const [start, setStart] = useState(false);
  const changed = async () => {
    await reload();
    await onChange();
  };
  const views: { key: View; icon: IconName; label: string; badge?: number; show: boolean }[] = [
    { key: 'mine', icon: 'contact', label: t('rev.v_mine'), badge: data ? data.counts.mine + data.counts.to_read : 0, show: true },
    { key: 'team', icon: 'users', label: t('rev.v_team'), badge: data?.counts.team ?? 0, show: !!data?.can.team },
    { key: 'all', icon: 'list', label: t('rev.v_all'), show: manage },
    { key: 'goals', icon: 'trendUp', label: t('rev.v_goals'), show: true },
    { key: 'templates', icon: 'file', label: t('rev.v_templates'), show: manage },
  ];
  return (
    <section className="panel rev-panel">
      <div className="panel-head rev-head">
        <div className="segmented" role="tablist" aria-label={t('rev.views')}>
          {views
            .filter((v) => v.show)
            .map((v) => (
              <button key={v.key} role="tab" aria-selected={view === v.key} className={view === v.key ? 'active' : ''} onClick={() => setView(v.key)}>
                <Icon name={v.icon} size={16} />
                {v.label}
                {v.badge ? <span className="hr-tab-badge">{v.badge}</span> : null}
              </button>
            ))}
        </div>
        {manage && view !== 'goals' && view !== 'templates' ? (
          <Button variant="primary" icon="plus" onClick={() => setStart(true)}>
            {t('rev.start')}
          </Button>
        ) : null}
      </div>
      {view === 'goals' ? (
        <GoalsView />
      ) : view === 'templates' ? (
        <TemplatesView />
      ) : (
        <>
          <div className="rev-tools">
            <div className="segmented" role="tablist" aria-label={t('rev.show')}>
              {(['active', 'done', 'cancelled'] as const).map((f) => (
                <button key={f} role="tab" aria-selected={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                  {t(`rev.f_${f}` as MessageKey)}
                </button>
              ))}
            </div>
            {data ? <span className="muted small">{tn('rev.reviews_n', data.reviews.length)}</span> : null}
          </div>
          {!data ? (
            <Skeleton h={220} r="var(--radius-lg)" />
          ) : data.reviews.length ? (
            <ul className="rev-list">
              {data.reviews.map((r) => (
                <ReviewRow key={r.id} r={r} view={view} me={me?.userId} onOpen={() => setOpen(r.id)} />
              ))}
            </ul>
          ) : (
            <EmptyState
              icon="star"
              title={filter === 'active' ? (view === 'mine' ? t('rev.empty_mine') : t('rev.empty_team')) : t('rev.empty_filter')}
              body={filter === 'active' ? (manage ? t('rev.empty_body_manage') : t('rev.empty_body')) : undefined}
              action={
                manage && filter === 'active' ? (
                  <Button variant="primary" icon="plus" onClick={() => setStart(true)}>
                    {t('rev.start')}
                  </Button>
                ) : undefined
              }
            />
          )}
        </>
      )}
      {open ? <ReviewDialog key={open} id={open} onClose={() => setOpen(null)} onChange={changed} /> : null}
      {start ? (
        <StartDialog
          onClose={() => setStart(false)}
          onStarted={async () => {
            setStart(false);
            setView('all');
            setFilter('active');
            await changed();
          }}
        />
      ) : null}
    </section>
  );
}

function ReviewRow({ r, view, me, onOpen }: { r: Row; view: View; me: string | undefined; onOpen: () => void }) {
  const step = nextStep(r, view, me);
  const other = view === 'mine' ? r.manager : r.employee;
  return (
    <li>
      <button type="button" className="rev-row" onClick={onOpen}>
        <Avatar name={other.name} photo={null} />
        <span className="rev-row-main">
          <strong>{view === 'mine' ? r.title : r.employee.name}</strong>
          <span className="muted small">
            {view === 'mine' ? t('rev.reviewer_is', { name: r.manager.name }) : view === 'all' ? t('rev.title_by', { title: r.title, name: r.manager.name }) : r.title}
            {' · '}
            {r.name}
          </span>
        </span>
        <span className="rev-row-steps">
          <Steps compact self={r.self_done} reviewer={r.manager_done} meeting={r.state === 'done' || (!!r.meeting_at && new Date(r.meeting_at) < new Date())} closed={r.state === 'done'} />
        </span>
        <span className="rev-row-when small">
          {r.state === 'done' ? (
            <Stars value={r.rating} size={14} />
          ) : r.meeting_at && r.state !== 'cancelled' ? (
            <span>
              <Icon name="calendar" size={14} /> {dateTime(r.meeting_at)}
            </span>
          ) : r.due ? (
            <span className={r.overdue ? 'text-danger' : 'muted'}>{r.overdue ? t('rev.overdue_since', { day: dayText(r.due) }) : t('rev.due', { day: dayText(r.due) })}</span>
          ) : null}
        </span>
        <span className="rev-row-badge">
          <StatusBadge tone={step.tone} icon={step.icon} label={step.label} />
        </span>
      </button>
    </li>
  );
}

// ---- one review ---------------------------------------------------------------------------

function ReviewDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { data: r, reload } = useAction<Detail>('hr.review', { id });
  const { busy, run } = useSave();
  const [cancel, setCancel] = useState(false);
  const [reason, setReason] = useState('');
  const [sendBack, setSendBack] = useState(false);
  const changed = async () => {
    await reload();
    await onChange();
  };
  const title = r ? `${r.title} · ${r.employee.name}` : t('rev.review');
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="xl"
      footer={
        r && (r.can.cancel || r.can.reopen) ? (
          <span className="foot-actions rev-foot">
            {r.can.cancel ? (
              <Button variant="ghost" icon="x" onClick={() => setCancel(true)}>
                {t('rev.cancel_review')}
              </Button>
            ) : null}
            {r.can.reopen ? (
              <Button variant="secondary" icon="undo" onClick={() => setSendBack(true)}>
                {t('rev.send_back')}
              </Button>
            ) : null}
          </span>
        ) : undefined
      }
    >
      {!r ? (
        <Skeleton h={360} r="var(--radius-lg)" />
      ) : (
        <div className="rev-detail">
          <div className="rev-person">
            <Avatar name={r.employee.name} photo={r.employee.photo} size="lg" />
            <div className="rev-person-who">
              <strong>{r.employee.name}</strong>
              <span className="muted small">{[r.employee.job_title, r.employee.department].filter(Boolean).join(' · ') || t('rev.no_job')}</span>
              <span className="muted small">
                {t('rev.reviewer_is', { name: r.manager.name })} · {r.name}
                {r.due ? ` · ${t('rev.due', { day: dayText(r.due) })}` : ''}
              </span>
            </div>
            <Steps self={!!r.self.done_at} reviewer={!!r.reviewer.done_at} meeting={r.state === 'done' || (!!r.meeting_at && new Date(r.meeting_at) < new Date())} closed={r.state === 'done'} />
          </div>
          <Guide r={r} />
          {r.state === 'done' || r.state === 'cancelled' ? <Outcome r={r} onChange={changed} /> : null}
          {r.can.answer ? <AnswerForm r={r} onChange={changed} /> : <Comparison r={r} />}
          {r.can.plan ? <MeetingRow r={r} onChange={changed} /> : r.meeting_at && r.state !== 'done' && r.state !== 'cancelled' ? <MeetingInfo at={r.meeting_at} /> : null}
          {r.can.close ? <CloseForm r={r} onChange={changed} /> : null}
          {r.goals.length ? (
            <section className="rev-section">
              <h3>{t('rev.goals_of', { name: r.employee.name })}</h3>
              <ul className="rev-goals compact">
                {r.goals.map((g) => (
                  <GoalLine key={g.id} g={g} />
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
      {cancel && r ? (
        <Dialog
          title={t('rev.cancel_title')}
          onClose={() => setCancel(false)}
          size="sm"
          footer={
            <span className="foot-actions">
              <Button
                variant="danger"
                icon="x"
                busy={busy}
                disabled={reason.trim().length < 3}
                onClick={() =>
                  void run(async () => {
                    await action('hr.cancel_review', { id: r.id, reason: reason.trim() });
                    setCancel(false);
                    await changed();
                  }, t('rev.cancelled'))
                }
              >
                {t('rev.cancel_review')}
              </Button>
            </span>
          }
        >
          <div className="form-stack">
            <p className="muted">{t('rev.cancel_body', { name: r.employee.name })}</p>
            <Field label={t('rev.cancel_reason')} required>
              <input className="input" value={reason} maxLength={300} placeholder={t('rev.cancel_reason_ph')} onChange={(e) => setReason(e.target.value)} data-autofocus />
            </Field>
          </div>
        </Dialog>
      ) : null}
      {sendBack && r ? (
        <Confirm
          title={t('rev.send_back_title')}
          body={t('rev.send_back_body', { name: r.employee.name })}
          confirm={t('rev.send_back')}
          busy={busy}
          onClose={() => setSendBack(false)}
          onConfirm={() =>
            void run(async () => {
              await action('hr.reopen_review_answers', { id: r.id });
              setSendBack(false);
              await changed();
            }, t('rev.sent_back'))
          }
        />
      ) : null}
    </Dialog>
  );
}

/** One line on what happens now, so nobody wonders why they cannot see something. */
function Guide({ r }: { r: Detail }) {
  let text = '';
  let icon: IconName = 'note';
  if (r.state === 'open') {
    const selfSent = !!r.self.done_at;
    const revSent = !!r.reviewer.done_at;
    if (r.me === 'employee') text = selfSent ? t('rev.g_emp_sent', { name: r.manager.name }) : t('rev.g_emp_answer', { name: r.manager.name });
    else if (r.me === 'manager')
      text = revSent ? t('rev.g_rev_sent', { name: r.employee.name }) : selfSent ? t('rev.g_rev_answer_ready', { name: r.employee.name }) : t('rev.g_rev_answer', { name: r.employee.name });
    else text = t('rev.g_admin_open');
    icon = 'lock';
  } else if (r.state === 'shared') {
    text = r.me === 'employee' ? t('rev.g_emp_shared', { name: r.manager.name }) : t('rev.g_rev_shared', { name: r.employee.name });
    icon = 'users';
  } else if (r.state === 'done' && !r.acknowledged_at) {
    text = r.me === 'employee' ? t('rev.g_emp_done') : t('rev.g_rev_done', { name: r.employee.name });
    icon = 'eye';
  }
  if (!text) return null;
  return (
    <p className="rev-guide">
      <Icon name={icon} size={16} />
      <span>{text}</span>
    </p>
  );
}

function AnswerForm({ r, onChange }: { r: Detail; onChange: () => Promise<void> }) {
  const side = r.me === 'employee' ? 'employee' : 'manager';
  const qs = r.questions.filter((q) => side === 'manager' || !q.manager_only);
  const start = (side === 'employee' ? r.self.answers : r.reviewer.answers) ?? {};
  const [a, setA] = useState<Answers>(start);
  const [tried, setTried] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { busy, run } = useSave();
  const missing = qs.filter((q) => (q.kind === 'rating' ? !a[q.id]?.rating : !a[q.id]?.text?.trim()));
  const set = (id: string, v: { rating?: number | null; text?: string | null }) => setA((x) => ({ ...x, [id]: { ...x[id], ...v } }));
  const answered = qs.length - missing.length;
  const save = (submit: boolean) =>
    run(
      async () => {
        await action('hr.answer_review', { id: r.id, answers: a, submit });
        setConfirm(false);
        await onChange();
      },
      submit ? t('rev.answers_sent') : t('rev.draft_saved'),
    );
  return (
    <section className="rev-section">
      <div className="rev-section-head">
        <h3>{side === 'employee' ? t('rev.your_side') : t('rev.reviewer_side', { name: r.employee.name })}</h3>
        <span className="muted small">{t('rev.answered', { n: answered, total: qs.length })}</span>
      </div>
      {r.intro ? <p className="rev-intro">{r.intro}</p> : null}
      <ol className="rev-questions">
        {qs.map((q, i) => {
          const bad = tried && missing.includes(q);
          return (
            <li key={q.id} className={bad ? 'invalid' : ''}>
              <div className="rev-q-text">
                <span className="rev-q-n">{i + 1}</span>
                <strong id={`rq-${q.id}`}>{q.text}</strong>
                {q.manager_only ? <span className="rev-only">{t('rev.reviewer_only')}</span> : null}
              </div>
              {q.kind === 'rating' ? (
                <>
                  <RatingInput value={a[q.id]?.rating} invalid={bad} label={q.text} onChange={(n) => set(q.id, { rating: n })} />
                  <textarea
                    className="input"
                    rows={2}
                    maxLength={4000}
                    value={a[q.id]?.text ?? ''}
                    placeholder={t('rev.why_optional')}
                    aria-label={t('rev.why_for', { q: q.text })}
                    onChange={(e) => set(q.id, { text: e.target.value })}
                  />
                </>
              ) : (
                <textarea className="input" rows={3} maxLength={4000} value={a[q.id]?.text ?? ''} aria-labelledby={`rq-${q.id}`} onChange={(e) => set(q.id, { text: e.target.value })} />
              )}
              {bad ? (
                <span className="field-msg" role="alert">
                  {q.kind === 'rating' ? t('rev.e_rating') : t('rev.e_text')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      <div className="rev-answer-actions">
        <span className="muted small">{t('rev.private_until_both')}</span>
        <Button variant="secondary" icon="check" busy={busy} onClick={() => void save(false)}>
          {t('rev.save_draft')}
        </Button>
        <Button
          variant="primary"
          icon="send"
          onClick={() => {
            setTried(true);
            if (!missing.length) setConfirm(true);
          }}
        >
          {t('rev.send_answers')}
        </Button>
      </div>
      {confirm ? (
        <Confirm
          title={t('rev.send_title')}
          body={side === 'employee' ? t('rev.send_body_emp', { name: r.manager.name }) : t('rev.send_body_rev', { name: r.employee.name })}
          confirm={t('rev.send_answers')}
          busy={busy}
          onClose={() => setConfirm(false)}
          onConfirm={() => void save(true)}
        />
      ) : null}
    </section>
  );
}

function AnswerCell({ q, a, sent, hidden }: { q: Question; a: Answers | null; sent: boolean; hidden: string }) {
  if (!a)
    return (
      <span className="rev-hidden">
        {sent ? <Icon name="lock" size={14} /> : <Icon name="clock" size={14} />} {hidden}
      </span>
    );
  const v = a[q.id];
  if (!v || (!v.rating && !v.text)) return <span className="muted">—</span>;
  return (
    <span className="rev-answer">
      {q.kind === 'rating' ? (
        <span className="rev-answer-rating">
          <Stars value={v.rating} size={14} />
          {v.rating ? <span className="small muted">{ratingLabel(v.rating)}</span> : null}
        </span>
      ) : null}
      {v.text ? <span className="rev-answer-text">{v.text}</span> : null}
    </span>
  );
}

function Comparison({ r }: { r: Detail }) {
  const selfHidden = r.self.done_at ? t('rev.hidden_sent') : t('rev.hidden_not_sent', { name: r.employee.name });
  const revHidden = r.reviewer.done_at ? t('rev.hidden_sent') : t('rev.hidden_not_sent', { name: r.manager.name });
  return (
    <section className="rev-section">
      <h3>{t('rev.answers')}</h3>
      {r.intro && r.state !== 'done' ? <p className="rev-intro">{r.intro}</p> : null}
      <ol className="rev-compare">
        {r.questions.map((q, i) => (
          <li key={q.id}>
            <div className="rev-q-text">
              <span className="rev-q-n">{i + 1}</span>
              <strong>{q.text}</strong>
              {q.manager_only ? <span className="rev-only">{t('rev.reviewer_only')}</span> : null}
            </div>
            <div className="rev-compare-sides">
              {q.manager_only ? null : (
                <div>
                  <span className="rev-side-label">{r.me === 'employee' ? t('rev.you') : r.employee.name}</span>
                  <AnswerCell q={q} a={r.self.answers} sent={!!r.self.done_at} hidden={selfHidden} />
                </div>
              )}
              <div>
                <span className="rev-side-label">{r.me === 'manager' ? t('rev.you') : r.manager.name}</span>
                <AnswerCell q={q} a={r.reviewer.answers} sent={!!r.reviewer.done_at} hidden={revHidden} />
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function MeetingInfo({ at }: { at: string }) {
  return (
    <p className="rev-meeting-info">
      <Icon name="calendar" size={16} />
      {t('rev.meeting_on', { at: dateTime(at) })}
    </p>
  );
}

function MeetingRow({ r, onChange }: { r: Detail; onChange: () => Promise<void> }) {
  const [at, setAt] = useState(r.meeting_at ? localInput(new Date(r.meeting_at)) : '');
  const { busy, run } = useSave();
  const changed = at !== (r.meeting_at ? localInput(new Date(r.meeting_at)) : '');
  return (
    <section className="rev-section rev-meeting">
      <div>
        <h3>{t('rev.meeting')}</h3>
        <p className="muted small">{r.meeting_at ? t('rev.meeting_on', { at: dateTime(r.meeting_at) }) : t('rev.meeting_hint')}</p>
      </div>
      <div className="rev-meeting-form">
        <input className="input" type="datetime-local" value={at} aria-label={t('rev.meeting_when')} onChange={(e) => setAt(e.target.value)} />
        <Button
          variant="secondary"
          icon="calendar"
          busy={busy}
          disabled={!changed}
          onClick={() =>
            void run(
              async () => {
                await action('hr.plan_review_meeting', { id: r.id, at: at ? new Date(at).toISOString() : null });
                await onChange();
              },
              at ? t('rev.meeting_saved') : t('rev.meeting_removed'),
            )
          }
        >
          {at || !r.meeting_at ? t('rev.meeting_save') : t('rev.meeting_remove')}
        </Button>
      </div>
    </section>
  );
}

function CloseForm({ r, onChange }: { r: Detail; onChange: () => Promise<void> }) {
  const [rating, setRating] = useState<number | null>(r.rating);
  const [summary, setSummary] = useState(r.summary ?? '');
  const [note, setNote] = useState(r.private_note ?? '');
  const [tried, setTried] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { busy, run } = useSave();
  const ok = !!rating && summary.trim().length >= 10;
  // A hint from both sides' ratings, never filled in for the reviewer.
  const avg = useMemo(() => {
    const n = r.questions
      .filter((q) => q.kind === 'rating')
      .map((q) => r.reviewer.answers?.[q.id]?.rating ?? 0)
      .filter(Boolean);
    return n.length ? Math.round((n.reduce((s, x) => s + x, 0) / n.length) * 10) / 10 : null;
  }, [r]);
  return (
    <section className="rev-section rev-close">
      <h3>{t('rev.close_title')}</h3>
      <p className="muted small">{t('rev.close_hint', { name: r.employee.name })}</p>
      <div className="form-stack">
        <Field label={t('rev.overall')} hint={avg ? t('rev.overall_hint', { n: avg }) : undefined} error={tried && !rating ? t('rev.e_overall') : null} required>
          <RatingInput value={rating} invalid={tried && !rating} label={t('rev.overall')} onChange={setRating} />
        </Field>
        <Field label={t('rev.summary')} hint={t('rev.summary_hint')} error={tried && summary.trim().length < 10 ? t('rev.e_summary') : null} required>
          <textarea className="input" rows={4} maxLength={4000} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>
        <Field label={t('rev.private_note')} hint={t('rev.private_note_hint', { name: r.employee.name })}>
          <textarea className="input" rows={2} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="rev-answer-actions">
          <Button
            variant="primary"
            icon="checkCircle"
            onClick={() => {
              setTried(true);
              if (ok) setConfirm(true);
            }}
          >
            {t('rev.close')}
          </Button>
        </div>
      </div>
      {confirm ? (
        <Confirm
          title={t('rev.close_confirm_title')}
          body={t('rev.close_confirm_body', { name: r.employee.name })}
          confirm={t('rev.close')}
          busy={busy}
          onClose={() => setConfirm(false)}
          onConfirm={() =>
            void run(async () => {
              await action('hr.close_review', { id: r.id, rating, summary: summary.trim(), private_note: note.trim() || null });
              setConfirm(false);
              await onChange();
            }, t('rev.closed'))
          }
        />
      ) : null}
    </section>
  );
}

function Outcome({ r, onChange }: { r: Detail; onChange: () => Promise<void> }) {
  const [comment, setComment] = useState('');
  const { busy, run } = useSave();
  if (r.state === 'cancelled')
    return (
      <section className="rev-outcome cancelled">
        <StatusBadge tone="neutral" icon="x" label={t('rev.s_cancelled')} />
        <p>{t('rev.cancelled_because', { reason: r.cancel_reason ?? '—' })}</p>
      </section>
    );
  return (
    <section className="rev-outcome">
      <div className="rev-outcome-head">
        <div>
          <span className="rev-side-label">{t('rev.overall')}</span>
          <span className="rev-outcome-rating">
            <Stars value={r.rating} size={20} />
            {r.rating ? <strong>{ratingLabel(r.rating)}</strong> : null}
          </span>
        </div>
        <span className="muted small">{r.closed_at ? t('rev.closed_on', { at: dateTime(r.closed_at), name: r.manager.name }) : null}</span>
      </div>
      {r.summary ? <p className="rev-summary">{r.summary}</p> : null}
      {r.private_note ? (
        <div className="rev-private">
          <span className="rev-side-label">
            <Icon name="lock" size={14} /> {t('rev.private_note')}
          </span>
          <p>{r.private_note}</p>
          <span className="muted small">{t('rev.private_note_hint', { name: r.employee.name })}</span>
        </div>
      ) : null}
      {r.acknowledged_at ? (
        <div className="rev-ack">
          <StatusBadge tone="ok" icon="checkCircle" label={t('rev.ack_on', { at: dateTime(r.acknowledged_at) })} />
          {r.employee_comment ? <p className="rev-comment">“{r.employee_comment}”</p> : null}
        </div>
      ) : r.can.acknowledge ? (
        <div className="rev-ack-form">
          <Field label={t('rev.your_comment')} hint={t('rev.your_comment_hint')}>
            <textarea className="input" rows={2} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
          </Field>
          <Button
            variant="primary"
            icon="checkCircle"
            busy={busy}
            onClick={() =>
              void run(async () => {
                await action('hr.acknowledge_review', { id: r.id, comment: comment.trim() || null });
                await onChange();
              }, t('rev.acknowledged'))
            }
          >
            {t('rev.acknowledge')}
          </Button>
        </div>
      ) : (
        <p className="muted small">{t('rev.not_ack_yet', { name: r.employee.name })}</p>
      )}
    </section>
  );
}

// ---- starting reviews -------------------------------------------------------------------

interface DirPerson {
  id: string;
  name: string;
  job_title: string | null;
  manager: string | null;
  photo: string | null;
}

function StartDialog({ onClose, onStarted }: { onClose: () => void; onStarted: () => Promise<void> }) {
  const { me } = useSession();
  const { toast } = useUi();
  const { data: tpls, reload: reloadTpls } = useAction<Template[]>('hr.review_templates');
  const { data: dir } = useAction<{ people: DirPerson[] }>('hr.directory');
  const { busy, run } = useSave();
  const [template, setTemplate] = useState('');
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [reviewer, setReviewer] = useState(me?.userId ?? '');
  const [q, setQ] = useState('');
  const [tried, setTried] = useState(false);
  const active = (tpls ?? []).filter((x) => x.active);
  const tpl = template || active[0]?.id || '';
  const people = dir?.people ?? [];
  const names = new Map(people.map((p) => [p.id, p.name]));
  const shown = people.filter((p) => !q.trim() || `${p.name} ${p.job_title ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const noManager = [...picked].filter((id) => !people.find((p) => p.id === id)?.manager).length;
  const okTitle = title.trim().length >= 3;
  return (
    <Dialog
      title={t('rev.start_title')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <span className="muted small">{tn('rev.people_picked', picked.size)}</span>
          <Button
            variant="primary"
            icon="send"
            busy={busy}
            disabled={!tpl}
            onClick={() => {
              setTried(true);
              if (!okTitle || !picked.size || !tpl) return;
              void run(async () => {
                const res = await action<{ created: number; skipped: string[] }>('hr.start_reviews', {
                  template: tpl,
                  title: title.trim(),
                  due: due || null,
                  people: [...picked],
                  reviewer: reviewer || null,
                });
                if (res.skipped.length) toast(t('rev.skipped', { names: res.skipped.join(', ') }), 'info');
                await onStarted();
              }, t('rev.started'));
            }}
          >
            {t('rev.start_n', { n: picked.size })}
          </Button>
        </span>
      }
    >
      {!tpls || !dir ? (
        <Skeleton h={300} r="var(--radius-lg)" />
      ) : !active.length ? (
        <EmptyState
          icon="file"
          title={t('rev.no_templates')}
          body={t('rev.no_templates_body')}
          action={
            <Button
              variant="primary"
              icon="plus"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('hr.add_default_review_template');
                  await reloadTpls();
                })
              }
            >
              {t('rev.add_default')}
            </Button>
          }
        />
      ) : (
        <div className="form-stack">
          <div className="form-grid">
            <Field label={t('rev.template')} required>
              <select className="input" value={tpl} onChange={(e) => setTemplate(e.target.value)}>
                {active.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} · {tn('rev.questions_n', x.questions.length)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('rev.title')} error={tried && !okTitle ? t('rev.e_title') : null} required>
              <input className="input" value={title} maxLength={80} placeholder={t('rev.title_ph')} onChange={(e) => setTitle(e.target.value)} data-autofocus />
            </Field>
            <Field label={t('rev.last_day')} hint={t('rev.optional')}>
              <input className="input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </Field>
            <Field label={t('rev.fallback_reviewer')} hint={t('rev.fallback_hint')}>
              <select className="input" value={reviewer} onChange={(e) => setReviewer(e.target.value)}>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <fieldset className="rev-pick">
            <legend className="field-label">
              {t('rev.who')} <span className="muted small">· {t('rev.who_hint')}</span>
            </legend>
            <div className="rev-pick-tools">
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" value={q} placeholder={t('rev.search_people')} aria-label={t('rev.search_people')} onChange={(e) => setQ(e.target.value)} />
              </label>
              <Button size="sm" variant="ghost" onClick={() => setPicked(new Set([...picked, ...shown.map((p) => p.id)]))}>
                {t('rev.pick_all')}
              </Button>
              <Button size="sm" variant="ghost" disabled={!picked.size} onClick={() => setPicked(new Set())}>
                {t('rev.pick_none')}
              </Button>
            </div>
            {tried && !picked.size ? (
              <span className="field-msg" role="alert">
                {t('rev.e_people')}
              </span>
            ) : null}
            <ul className="rev-pick-list">
              {shown.map((p) => (
                <li key={p.id}>
                  <label className={picked.has(p.id) ? 'on' : ''}>
                    <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
                    <Avatar name={p.name} photo={p.photo} size="sm" />
                    <span className="rev-pick-who">
                      <strong>{p.name}</strong>
                      <span className="muted small">
                        {p.manager
                          ? t('rev.reviewed_by', { name: names.get(p.manager) ?? '' })
                          : p.id === reviewer
                            ? t('rev.reviewed_by_nobody')
                            : t('rev.reviewed_by_fallback', { name: names.get(reviewer) ?? '' })}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            {noManager ? <p className="muted small">{tn('rev.no_manager_n', noManager, { name: names.get(reviewer) ?? '' })}</p> : null}
          </fieldset>
        </div>
      )}
    </Dialog>
  );
}

// ---- templates ----------------------------------------------------------------------------

function TemplatesView() {
  const { data, reload } = useAction<Template[]>('hr.review_templates');
  const [edit, setEdit] = useState<Template | 'new' | null>(null);
  const { busy, run } = useSave();
  if (!data) return <Skeleton h={220} r="var(--radius-lg)" />;
  return (
    <>
      <div className="rev-tools">
        <p className="muted small rev-tools-text">{t('rev.templates_hint')}</p>
        <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
          {t('rev.new_template')}
        </Button>
      </div>
      {data.length ? (
        <ul className="rev-templates">
          {data.map((x) => (
            <li key={x.id}>
              <button type="button" className="rev-template" onClick={() => setEdit(x)}>
                <span className="rev-template-icon tone-purple">
                  <Icon name="file" size={18} />
                </span>
                <span className="rev-template-main">
                  <strong>{x.name}</strong>
                  <span className="muted small">
                    {tn('rev.questions_n', x.questions.length)} · {tn('rev.reviews_n', x.reviews)}
                  </span>
                </span>
                {x.active ? <StatusBadge tone="ok" icon="checkCircle" label={t('rev.in_use')} /> : <StatusBadge tone="neutral" icon="archive" label={t('rev.not_in_use')} />}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon="file"
          title={t('rev.no_templates')}
          body={t('rev.no_templates_body')}
          action={
            <Button
              variant="primary"
              icon="plus"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('hr.add_default_review_template');
                  await reload();
                }, t('rev.template_saved'))
              }
            >
              {t('rev.add_default')}
            </Button>
          }
        />
      )}
      {edit ? (
        <TemplateForm
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
          }}
        />
      ) : null}
    </>
  );
}

function TemplateForm({ initial, onClose, onSaved }: { initial: Template | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(initial?.name ?? '');
  const [intro, setIntro] = useState(initial?.intro ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [qs, setQs] = useState<Question[]>(initial?.questions ?? [{ id: 'q1', kind: 'rating', text: '', manager_only: false }]);
  const [tried, setTried] = useState(false);
  const nextId = () => `q${Math.max(0, ...qs.map((q) => Number(q.id.replace(/\D/g, '')) || 0)) + 1}`;
  const set = (i: number, v: Partial<Question>) => setQs((x) => x.map((q, j) => (j === i ? { ...q, ...v } : q)));
  const move = (i: number, d: -1 | 1) =>
    setQs((x) => {
      const n = [...x];
      [n[i], n[i + d]] = [n[i + d]!, n[i]!];
      return n;
    });
  const okName = name.trim().length >= 2;
  const okQs = qs.length > 0 && qs.every((q) => q.text.trim().length >= 3) && qs.some((q) => !q.manager_only);
  return (
    <Dialog
      title={initial ? t('rev.edit_template') : t('rev.new_template')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          {initial?.reviews ? <span className="muted small">{t('rev.template_used_hint')}</span> : null}
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!okName || !okQs) return;
              void run(async () => {
                await action('hr.save_review_template', { id: initial?.id ?? null, name: name.trim(), intro: intro.trim() || null, active, questions: qs.map((q) => ({ ...q, text: q.text.trim() })) });
                await onSaved();
              }, t('rev.template_saved'));
            }}
          >
            {t('rev.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('rev.template_name')} error={tried && !okName ? t('rev.e_template_name') : null} required>
            <input className="input" value={name} maxLength={80} placeholder={t('rev.template_name_ph')} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
          <div className="rev-switch-field">
            <Switch checked={active} onChange={setActive} label={t('rev.in_use')} />
            <span>
              <strong>{t('rev.in_use')}</strong>
              <span className="muted small">{t('rev.in_use_hint')}</span>
            </span>
          </div>
        </div>
        <Field label={t('rev.intro')} hint={t('rev.intro_hint')}>
          <textarea className="input" rows={2} maxLength={1000} value={intro} onChange={(e) => setIntro(e.target.value)} />
        </Field>
        <fieldset className="rev-q-editor">
          <legend className="field-label">{t('rev.questions')}</legend>
          {tried && !okQs ? (
            <span className="field-msg" role="alert">
              {qs.some((q) => !q.manager_only) ? t('rev.e_questions') : t('rev.e_one_for_person')}
            </span>
          ) : null}
          <ol>
            {qs.map((q, i) => (
              <li key={q.id}>
                <span className="rev-q-n">{i + 1}</span>
                <div className="rev-q-fields">
                  <input
                    className={`input${tried && q.text.trim().length < 3 ? ' invalid' : ''}`}
                    value={q.text}
                    maxLength={200}
                    placeholder={t('rev.question_ph')}
                    aria-label={t('rev.question_n', { n: i + 1 })}
                    onChange={(e) => set(i, { text: e.target.value })}
                  />
                  <div className="rev-q-opts">
                    <select className="input" value={q.kind} aria-label={t('rev.answer_kind')} onChange={(e) => set(i, { kind: e.target.value as Question['kind'] })}>
                      <option value="rating">{t('rev.kind_rating')}</option>
                      <option value="text">{t('rev.kind_text')}</option>
                    </select>
                    <label className="rev-q-only">
                      <Switch checked={q.manager_only} onChange={(v) => set(i, { manager_only: v })} label={t('rev.reviewer_only')} />
                      <span className="small">{t('rev.reviewer_only')}</span>
                    </label>
                  </div>
                </div>
                <span className="rev-q-move">
                  <IconButton icon="up" label={t('rev.move_up')} disabled={i === 0} onClick={() => move(i, -1)} />
                  <IconButton icon="down" label={t('rev.move_down')} disabled={i === qs.length - 1} onClick={() => move(i, 1)} />
                  <IconButton icon="trash" label={t('rev.remove_question')} disabled={qs.length === 1} onClick={() => setQs((x) => x.filter((_, j) => j !== i))} />
                </span>
              </li>
            ))}
          </ol>
          <Button size="sm" variant="secondary" icon="plus" disabled={qs.length >= 20} onClick={() => setQs((x) => [...x, { id: nextId(), kind: 'text', text: '', manager_only: false }])}>
            {t('rev.add_question')}
          </Button>
        </fieldset>
      </div>
    </Dialog>
  );
}

// ---- goals --------------------------------------------------------------------------------

interface GoalList {
  user: string;
  today: string;
  goals: Goal[];
  team: { id: string; name: string }[];
}

function GoalLine({ g, onOpen, actions }: { g: Goal; onOpen?: () => void; actions?: ReactNode }) {
  const tone: Tone = g.state === 'done' ? 'ok' : g.state === 'cancelled' ? 'neutral' : g.overdue ? 'danger' : 'info';
  const body = (
    <>
      <span className="rev-goal-main">
        <strong>{g.title}</strong>
        {g.description ? <span className="muted small rev-goal-desc">{g.description}</span> : null}
        <span className="rev-goal-meta small">
          {g.state === 'open' ? (
            g.due ? (
              <span className={g.overdue ? 'text-danger' : 'muted'}>{g.overdue ? t('rev.overdue_since', { day: dayText(g.due) }) : t('rev.due', { day: dayText(g.due) })}</span>
            ) : (
              <span className="muted">{t('rev.no_due')}</span>
            )
          ) : (
            <StatusBadge tone={tone} icon={g.state === 'done' ? 'checkCircle' : 'x'} label={t(`rev.goal_${g.state}` as MessageKey)} />
          )}
        </span>
      </span>
      <span className="rev-goal-progress">
        <span
          className={`tk-progress${g.overdue ? ' warn' : ''}`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={g.progress}
          aria-label={t('rev.progress_of', { title: g.title })}
        >
          <span style={{ width: `${g.progress}%` }} />
        </span>
        <span className="small">{g.progress} %</span>
      </span>
    </>
  );
  return (
    <li className={`rev-goal is-${g.state}`}>
      {onOpen ? (
        <button type="button" className="rev-goal-open" onClick={onOpen}>
          {body}
        </button>
      ) : (
        <div className="rev-goal-open">{body}</div>
      )}
      {actions ? <span className="rev-goal-actions">{actions}</span> : null}
    </li>
  );
}

function GoalsView() {
  const { me } = useSession();
  const [user, setUser] = useState<string>('');
  const { data, reload } = useAction<GoalList>('hr.goals', { user: user || null });
  const [edit, setEdit] = useState<Goal | 'new' | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const { run } = useSave();
  if (!data) return <Skeleton h={220} r="var(--radius-lg)" />;
  const open = data.goals.filter((g) => g.state === 'open');
  const closed = data.goals.filter((g) => g.state !== 'open');
  const who = data.team.find((p) => p.id === data.user)?.name;
  const setGoal = (g: Goal, patch: { state?: Goal['state']; progress?: number }, msg: string) =>
    void run(async () => {
      await action('hr.set_goal', { id: g.id, ...patch });
      await reload();
    }, msg);
  return (
    <>
      <div className="rev-tools">
        {data.team.length ? (
          <select className="input rev-who" value={user} aria-label={t('rev.goals_for')} onChange={(e) => setUser(e.target.value)}>
            <option value="">{t('rev.my_goals')}</option>
            {data.team.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        ) : (
          <strong>{t('rev.my_goals')}</strong>
        )}
        <span className="muted small">{tn('rev.open_goals_n', open.length)}</span>
        <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
          {t('rev.new_goal')}
        </Button>
      </div>
      {open.length ? (
        <ul className="rev-goals">
          {open.map((g) => (
            <GoalLine
              key={g.id}
              g={g}
              onOpen={() => setEdit(g)}
              actions={
                <>
                  <IconButton icon="plus" label={t('rev.add_10')} disabled={g.progress >= 100} onClick={() => setGoal(g, { progress: Math.min(100, g.progress + 10) }, t('rev.goal_saved'))} />
                  <IconButton icon="checkCircle" label={t('rev.mark_done')} onClick={() => setGoal(g, { state: 'done' }, t('rev.goal_done_toast'))} />
                </>
              }
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          icon="trendUp"
          title={who ? t('rev.no_goals_for', { name: who }) : t('rev.no_goals')}
          body={t('rev.no_goals_body')}
          action={
            <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
              {t('rev.new_goal')}
            </Button>
          }
        />
      )}
      {closed.length ? (
        <div className="rev-closed-goals">
          <button type="button" className="link-button" aria-expanded={showClosed} onClick={() => setShowClosed((v) => !v)}>
            <Icon name={showClosed ? 'up' : 'down'} size={14} /> {tn('rev.closed_goals_n', closed.length)}
          </button>
          {showClosed ? (
            <ul className="rev-goals">
              {closed.map((g) => (
                <GoalLine key={g.id} g={g} actions={<IconButton icon="undo" label={t('rev.reopen_goal')} onClick={() => setGoal(g, { state: 'open' }, t('rev.goal_reopened'))} />} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {edit ? (
        <GoalForm
          user={data.user}
          initial={edit === 'new' ? null : edit}
          forName={data.user === me?.userId ? null : (who ?? null)}
          onClose={() => setEdit(null)}
          onCancelGoal={
            edit !== 'new'
              ? () => {
                  setEdit(null);
                  setGoal(edit, { state: 'cancelled' }, t('rev.goal_cancelled_toast'));
                }
              : undefined
          }
          onSaved={async () => {
            setEdit(null);
            await reload();
          }}
        />
      ) : null}
    </>
  );
}

function GoalForm({
  user,
  initial,
  forName,
  onClose,
  onSaved,
  onCancelGoal,
}: {
  user: string;
  initial: Goal | null;
  forName: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onCancelGoal?: () => void;
}) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ title: initial?.title ?? '', description: initial?.description ?? '', due: initial?.due ?? '', progress: initial?.progress ?? 0 });
  const [tried, setTried] = useState(false);
  const ok = f.title.trim().length >= 3;
  return (
    <Dialog
      title={initial ? t('rev.edit_goal') : forName ? t('rev.new_goal_for', { name: forName }) : t('rev.new_goal')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          {onCancelGoal ? (
            <Button variant="ghost" icon="x" onClick={onCancelGoal}>
              {t('rev.cancel_goal')}
            </Button>
          ) : null}
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!ok) return;
              void run(async () => {
                await action('hr.save_goal', { id: initial?.id ?? null, user, title: f.title.trim(), description: f.description.trim() || null, due: f.due || null, progress: f.progress });
                await onSaved();
              }, t('rev.goal_saved'));
            }}
          >
            {t('rev.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('rev.goal_title')} error={tried && !ok ? t('rev.e_goal_title') : null} required>
          <input className="input" value={f.title} maxLength={120} placeholder={t('rev.goal_title_ph')} onChange={(e) => setF({ ...f, title: e.target.value })} data-autofocus />
        </Field>
        <Field label={t('rev.goal_description')} hint={t('rev.optional')}>
          <textarea className="input" rows={3} maxLength={2000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </Field>
        <div className="form-grid">
          <Field label={t('rev.goal_due')} hint={t('rev.optional')}>
            <input className="input" type="date" value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} />
          </Field>
          <Field label={t('rev.goal_progress', { n: f.progress })}>
            <input className="rev-range" type="range" min={0} max={100} step={5} value={f.progress} onChange={(e) => setF({ ...f, progress: Number(e.target.value) })} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
